import { Layout } from '../components/Layout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Tags, X, Download, Upload, Ban } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useActiveLocation } from '../context/LocationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const empty = { item_id: '', name: '', category: '', price: '', gst_rate: '5', hsn_code: '', deduct_type: 'none', deduct_ref: '', is_active: true, opening_stock: '', stock_location: '' };

export default function Menu({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(empty);
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [stockMap, setStockMap] = useState({});
  const [selected, setSelected] = useState(() => new Set());

  const { activeLocation } = useActiveLocation();
  const locationId = activeLocation?.location_id || user?.outlet_id || 'central_kitchen';
  const LOW_STOCK = 5;
  const loadStock = async () => {
    try {
      const [areasR, stockR] = await Promise.all([
        axios.get(`${API}/storage-areas?location_id=${locationId}`, auth()),
        axios.get(`${API}/inventory/stock?location_id=${locationId}`, auth()),
      ]);
      const saleId = (areasR.data || []).find((a) => a.is_default_sale)?.area_id;
      const m = {};
      (stockR.data || []).forEach((s) => {
        if (!saleId || s.area_id === saleId) m[s.product_id] = (m[s.product_id] || 0) + s.quantity;
      });
      setStockMap(m);
    } catch (e) { /* noop */ }
  };
  const itemStock = (it) => (it.deduct_type === 'product' && it.deduct_ref && stockMap[it.deduct_ref] !== undefined) ? stockMap[it.deduct_ref] : null;

  const load = () => {
    axios.get(`${API}/menu`, auth()).then((r) => setItems(r.data)).catch(() => {});
  };
  const loadCategories = () => {
    axios.get(`${API}/menu/categories`, auth()).then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    loadCategories();
    axios.get(`${API}/products`, auth()).then((r) => setProducts(r.data)).catch(() => {});
    axios.get(`${API}/recipes`, auth()).then((r) => setRecipes(r.data)).catch(() => {});
    axios.get(`${API}/locations`, auth()).then((r) => setLocations(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadStock(); /* eslint-disable-next-line */ }, [locationId]);

  const openNew = () => { setForm({ ...empty, item_id: `menu_${Date.now()}`, category: categories[0] || '' }); setEditing(false); setOpen(true); };
  const openEdit = (it) => {
    const cur = itemStock(it);
    setForm({
      ...it, price: String(it.price), gst_rate: String(it.gst_rate), hsn_code: it.hsn_code || '', deduct_ref: it.deduct_ref || '',
      opening_stock: cur != null ? String(cur) : '', stock_location: locationId,
    });
    setEditing(true); setOpen(true);
  };

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/menu/categories`, { name }, auth());
      toast.success('Category added');
      setNewCat('');
      loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add category');
    }
  };

  const deleteCategory = async (name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await axios.delete(`${API}/menu/categories/${encodeURIComponent(name)}`, auth());
      toast.success('Category deleted');
      loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete category');
    }
  };

  const save = async () => {
    const payload = {
      ...form,
      price: parseFloat(form.price),
      gst_rate: parseFloat(form.gst_rate),
      hsn_code: form.hsn_code || null,
      opening_stock: (!editing && form.opening_stock !== '' && form.opening_stock != null) ? parseFloat(form.opening_stock) : null,
      stock_location: (!editing && form.opening_stock !== '' && form.opening_stock != null) ? (form.stock_location || null) : null,
    };
    if (payload.opening_stock != null && !payload.stock_location) {
      toast.error('Select a location for the opening stock');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/menu/${form.item_id}`, payload, auth());
        // Apply stock at the active location (set to the entered quantity)
        if (form.opening_stock !== '' && form.opening_stock != null) {
          await axios.post(`${API}/menu/${form.item_id}/stock`, { quantity: parseFloat(form.opening_stock), location_id: locationId, mode: 'set' }, auth());
        }
      } else {
        await axios.post(`${API}/menu`, payload, auth());
      }
      toast.success(`Item ${editing ? 'updated' : 'created'}`);
      setOpen(false);
      load();
      loadStock();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await axios.delete(`${API}/menu/${id}`, auth()); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const sortedItems = [...items].sort((a, b) => (a.category || '').localeCompare(b.category || '') || (a.name || '').localeCompare(b.name || ''));
  const toggleRow = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = sortedItems.length > 0 && sortedItems.every((it) => selected.has(it.item_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(sortedItems.map((it) => it.item_id)));
  const clearSelection = () => setSelected(new Set());

  const bulkMarkOutOfStock = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} item(s) as OUT OF STOCK at ${activeLocation?.name || locationId}? This sets their counter stock to 0.`)) return;
    try {
      const res = await axios.post(`${API}/menu/bulk-stock`, { item_ids: [...selected], location_id: locationId, quantity: 0, mode: 'set' }, auth());
      toast.success(`Marked ${res.data.updated} item(s) out of stock`);
      clearSelection();
      loadStock();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const downloadTemplate = () => {
    const header = 'name,category,price,gst_rate,opening_stock,stock_location';
    const sample = [
      'Milk & Coconut Pastry,Pastry,120,5,20,Central Kitchen',
      'Almond Croissant,Pastry,90,5,,',
    ];
    const csv = [header, ...sample].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'menu_products_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
      const cells = line.split(',');
      const o = {};
      headers.forEach((h, i) => { o[h] = (cells[i] || '').trim(); });
      return o;
    });
  };

  const bulkUpload = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (!rows.length) return toast.error('No rows found in file');
      const itemsToSend = rows.map((r) => ({
        name: r.name,
        category: r.category || 'Uncategorised',
        price: parseFloat(r.price || 0),
        gst_rate: r.gst_rate ? parseFloat(r.gst_rate) : 5,
        opening_stock: r.opening_stock !== '' && r.opening_stock != null ? parseFloat(r.opening_stock) : null,
        stock_location: r.stock_location || null,
      }));
      const res = await axios.post(`${API}/menu/bulk`, { items: itemsToSend }, auth());
      const { created, failed, errors } = res.data;
      if (failed > 0) {
        toast.warning(`Imported ${created}, ${failed} failed. Row ${errors[0].row}: ${errors[0].error}`);
      } else {
        toast.success(`Imported ${created} product(s)`);
      }
      load(); loadCategories(); loadStock();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk upload failed');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-stone-900">Menu / Catalog</h1>
            <p className="text-stone-600 mt-1">Manage sellable products & prices</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="download-template-button" onClick={downloadTemplate} variant="outline" className="border-stone-300">
              <Download size={18} className="mr-2" /> Template
            </Button>
            <label data-testid="bulk-upload-button" className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium border border-stone-300 px-4 py-2 hover:bg-stone-50">
              <Upload size={18} className="mr-2" /> Bulk Upload
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { bulkUpload(e.target.files[0]); e.target.value = ''; }} />
            </label>
            <Button data-testid="manage-categories-button" onClick={() => setCatOpen(true)} variant="outline" className="border-stone-300">
              <Tags size={18} className="mr-2" /> Categories
            </Button>
            <Button data-testid="add-menu-button" onClick={openNew} className="bg-secondary hover:bg-secondary-hover text-black">
              <Plus size={20} className="mr-2" /> Add Item
            </Button>
          </div>
        </div>

        {selected.size > 0 && (
          <div data-testid="menu-bulk-bar" className="mb-3 flex items-center gap-3 bg-functional-error/5 border border-functional-error/30 rounded-sm px-4 py-2">
            <span className="text-sm text-stone-700" data-testid="menu-bulk-count">{selected.size} selected</span>
            <Button data-testid="menu-bulk-oos-button" onClick={bulkMarkOutOfStock} size="sm" className="bg-functional-error hover:bg-functional-error/90 text-white h-8">
              <Ban size={15} className="mr-1" /> Mark Out of Stock
            </Button>
            <button data-testid="menu-bulk-clear" onClick={clearSelection} className="text-stone-400 hover:text-stone-700 ml-auto" title="Clear selection"><X size={16} /></button>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input data-testid="menu-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-primary cursor-pointer" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Category</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-stone-500 uppercase">Price</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-stone-500 uppercase">GST%</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-stone-500 uppercase">Stock ({activeLocation?.name || locationId})</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-stone-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => (
                <tr key={it.item_id} className={`border-b border-stone-100 hover:bg-stone-50 ${selected.has(it.item_id) ? 'bg-secondary/5' : ''}`} data-testid={`menu-row-${it.item_id}`}>
                  <td className="px-4 py-3">
                    <input data-testid={`menu-select-${it.item_id}`} type="checkbox" checked={selected.has(it.item_id)} onChange={() => toggleRow(it.item_id)} className="h-4 w-4 accent-primary cursor-pointer" />
                  </td>
                  <td className="px-4 py-3 font-medium">{it.name}</td>
                  <td className="px-4 py-3 text-stone-600">{it.category}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{Number(it.price).toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">{it.gst_rate}%</td>
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const s = itemStock(it);
                      if (s === null) return <span className="text-stone-300 text-xs">—</span>;
                      if (s <= 0) return <span data-testid={`menu-stock-out-${it.item_id}`} className="bg-functional-error/10 text-functional-error text-xs font-semibold px-2 py-0.5 rounded-full">Out ({s})</span>;
                      if (s <= LOW_STOCK) return <span data-testid={`menu-stock-low-${it.item_id}`} className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">Low: {s}</span>;
                      return <span data-testid={`menu-stock-${it.item_id}`} className="text-stone-700 text-sm font-mono">{s}</span>;
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-menu-${it.item_id}`} onClick={() => openEdit(it)} className="text-stone-500 hover:text-primary mr-3"><Pencil size={16} /></button>
                    <button data-testid={`delete-menu-${it.item_id}`} onClick={() => del(it.item_id)} className="text-stone-500 hover:text-functional-error"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-stone-400">No items yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Menu Item</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
              <Input data-testid="menu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Category</label>
                <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="menu-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{categories.filter((c) => c && String(c).trim()).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Price (₹, incl. GST)</label>
                <Input data-testid="menu-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">GST Rate (%)</label>
              <Select value={form.gst_rate} onValueChange={(v) => setForm({ ...form, gst_rate: v })}>
                <SelectTrigger data-testid="menu-gst"><SelectValue /></SelectTrigger>
                <SelectContent>{['0', '5', '12', '18', '28'].map((g) => <SelectItem key={g} value={g}>{g}%</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">{editing ? 'Set Stock (pcs)' : 'Stock Quantity (pcs)'}</label>
                <Input data-testid="menu-stock-qty" type="number" min="0" placeholder="e.g. 20" value={form.opening_stock} onChange={(e) => setForm({ ...form, opening_stock: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Stock Location</label>
                {editing ? (
                  <div data-testid="menu-stock-location-fixed" className="h-10 flex items-center px-3 rounded-md border border-stone-200 bg-stone-50 text-sm text-stone-700">
                    {activeLocation?.name || locationId}
                  </div>
                ) : (
                  <Select value={form.stock_location || undefined} onValueChange={(v) => setForm({ ...form, stock_location: v })}>
                    <SelectTrigger data-testid="menu-stock-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>{locations.filter((l) => l.location_id).map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <p className="text-xs text-stone-500 -mt-1">
              {editing
                ? `Sets the current stock for this item at ${activeLocation?.name || locationId}. Stock auto-deducts on every sale.`
                : 'Stock auto-deducts on every sale. Set an opening quantity & location to start tracking — leave blank to start at zero.'}
            </p>
          </div>
          <Button data-testid="save-menu-button" onClick={save} className="w-full bg-primary text-white">Save</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage Categories</DialogTitle></DialogHeader>
          <div className="flex gap-2 py-2">
            <Input
              data-testid="new-category-input"
              placeholder="New category name"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
            />
            <Button data-testid="add-category-button" onClick={addCategory} className="bg-primary text-white shrink-0">
              <Plus size={18} className="mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {categories.map((c) => (
              <div key={c} data-testid={`category-row-${c}`} className="flex items-center justify-between border border-stone-200 rounded-sm px-3 py-2">
                <span className="text-sm font-medium text-stone-800">{c}</span>
                <button data-testid={`delete-category-${c}`} onClick={() => deleteCategory(c)} className="text-stone-400 hover:text-functional-error">
                  <X size={16} />
                </button>
              </div>
            ))}
            {categories.length === 0 && <p className="text-center text-stone-400 py-6 text-sm">No categories yet</p>}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
